import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import errorIcon from "../../../../assets/images/error-2.png";
import successIcon from "../../../../assets/images/success_lg.png";
import Loader from "../../common/Loader";
import NmsTableSearch, { searchRows, sortRows } from "../../common/NmsTableSearch";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";
import AddSSIDModal from "./addSSIDModal";
import FastRoaming from "./fastRoaming";
import LoadBalancing from "./loadBalancing";
import MeshSettings from "./meshSettings";
import RadioConfig from "./RadioConfig";
import TrafficPolicies from "./trafficPolicies";
import TrafficShaping from "./trafficShaping";
import UrlFiltering from "./urlFiltering";
import VlanManagement from "./vlanManagement";
import WifiSchedule from "./wifiSchedule";
import IpSettings from "./wirelessIpSettings";

const PAGE_SIZE = 10;
const SSID_FIELDS = [
  "ssid",
  "band",
  "securityAuthentication",
  "vlanId",
  "broadcastStatus",
  "fastRoamingSt",
];

const TABS = [
  {
    group: "Wi-Fi",
    items: [
      { id: "wifi", labelKey: "wifi", fallback: "SSIDs" },
      { id: "schedule", labelKey: "wifiSchedules", fallback: "Wi-Fi schedules" },
      { id: "radioConfig", labelKey: "radioConfig", fallback: "Radio configuration" },
      { id: "meshSettings", labelKey: "meshSettings", fallback: "Mesh settings" },
    ],
  },
  {
    group: "Access",
    items: [
      { id: "roaming", labelKey: "fastRoaming", fallback: "Fast roaming" },
      { id: "balancing", labelKey: "loadBalancing", fallback: "Load balancing" },
      { id: "filtering", labelKey: "urlFiltering", fallback: "URL filtering" },
      { id: "trafficShaping", labelKey: "trafficShaping", fallback: "Traffic shaping" },
    ],
  },
  {
    group: "Network",
    items: [
      { id: "management", labelKey: "vlanManagement", fallback: "VLAN management" },
      { id: "ipSettings", labelKey: "ipSettings", fallback: "IP settings" },
    ],
  },
];

const asList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.response)) return value.response;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const bandText = (value) => String(value || "").replace(/\s+/g, "").toUpperCase();

function WifiConfig() {
  const navigate = useNavigate();
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;

  const [activeTab, setActiveTab] = useState("wifi");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [SSID, setSSID] = useState([]);
  const [tableQuery, setTableQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [page, setPage] = useState(1);
  const [schedulesList, setSchedulesList] = useState([]);
  const [schedulePage, setSchedulePage] = useState(1);
  const [ssid, setSsid] = useState("");
  const [wnId, setWnId] = useState("");
  const [scheduleId, setScheduleId] = useState("");
  const [addUrl, setAddUrl] = useState(false);
  const [scheduleDeleteStatus, setScheduleDeleteStatus] = useState(null);
  const [showScheduleStatusModal, setShowScheduleStatusModal] = useState(false);

  const current = TABS.flatMap((group) => group.items).find((tab) => tab.id === activeTab);
  const currentTitle =
    localizationStrings?.[current?.labelKey] || current?.fallback || localizationStrings?.wifiConfiguration;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setIsScheduleModalOpen(false);
  };

  const getSSIDlist = async (id = networkId, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/configuration/getAllSsids?networkId=${id}`
      );
      setSSID(asList(response));
    } catch (error) {
      console.error("Error fetching SSIDs:", error);
      setSSID([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSchedules = async (id = networkId) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wireless/configuration/getAllSsidSchedule?networkId=${id}`
      );
      const list = asList(response).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setSchedulesList(list);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setSchedulesList([]);
    }
  };

  useEffect(() => {
    if (networkId) getSSIDlist(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  useEffect(() => {
    if (activeTab === "schedule" && networkId) fetchSchedules(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, networkId]);

  useEffect(() => {
    setPage(1);
  }, [tableQuery, sortConfig.key, sortConfig.direction, networkId]);

  useEffect(() => {
    setSchedulePage(1);
  }, [networkId, activeTab]);

  const visibleSsids = useMemo(
    () =>
      sortRows(searchRows(SSID, tableQuery, SSID_FIELDS), sortConfig.key, sortConfig.direction),
    [SSID, tableQuery, sortConfig]
  );

  const totalPages = Math.max(1, Math.ceil(visibleSsids.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visibleSsids.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const visibleSchedules = useMemo(
    () => searchRows(schedulesList, tableQuery, ["scheduleName", "scheduleStatus"]),
    [schedulesList, tableQuery]
  );
  const totalSchedulePages = Math.max(1, Math.ceil(visibleSchedules.length / PAGE_SIZE));
  const currentSchedulePage = Math.min(schedulePage, totalSchedulePages);
  const scheduleRows = visibleSchedules.slice(
    (currentSchedulePage - 1) * PAGE_SIZE,
    currentSchedulePage * PAGE_SIZE
  );

  const fetchSSIDDetails = async (id) => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/wireless/configuration/getSsid?wnId=${id}`);
      if (response?.response) {
        navigate("/wifi-settings", { state: { ssidDetails: response.response } });
      }
    } catch (error) {
      console.error("Error fetching SSID details:", error);
    }
  };

  const deleteSSID = async (id) => {
    try {
      const api = new allApi();
      await api.deleteRequest(
        `api/wireless/configuration/deleteSsid?networkId=${networkId}&wnId=${id}`
      );
      await getSSIDlist(networkId, { silent: true });
    } catch (error) {
      console.error("Error deleting SSID:", error);
    }
  };

  const deleteSchedule = async (id) => {
    try {
      const api = new allApi();
      const response = await api.deleteRequest(
        `api/wireless/configuration/deleteSchedule?networkId=${networkId}`,
        [{ scheduleId: id }]
      );
      setScheduleDeleteStatus(response?.status === "OK" ? "success" : "error");
      if (response?.status === "OK") fetchSchedules(networkId);
    } catch (error) {
      console.error("Error deleting schedule:", error);
      setScheduleDeleteStatus("error");
    } finally {
      setShowScheduleStatusModal(true);
    }
  };

  const toggleSort = (field) => {
    setSortConfig((prev) => ({
      key: field,
      direction: prev.key === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortHeader = (field, label) => (
    <th
      className={`inc-th-sort${sortConfig.key === field ? " is-active" : ""}`}
      onClick={() => toggleSort(field)}
    >
      {label}
      <i className="fa fa-sort" aria-hidden="true" />
    </th>
  );

  return (
    <div className="nms-dash config-page">
      <div className="inc-card ch-100">
        <div className="inc-card-title">
          <h3>{currentTitle}</h3>
          <div className="inc-card-button">
            <button className="btn btn-outline" onClick={() => navigate("/wireless")}>
              {localizationStrings?.quickView}
            </button>
            {activeTab === "wifi" && (
              <>
                <NmsTableSearch
                  value={tableQuery}
                  onChange={setTableQuery}
                  placeholder={localizationStrings?.search || "Search SSIDs"}
                />
                <button
                  className="icon-btn"
                  title="Refresh"
                  onClick={() => getSSIDlist(networkId, { silent: true })}
                >
                  <i className={`fa ${refreshing ? "fa-spinner fa-spin" : "fa-refresh"}`} aria-hidden="true" />
                </button>
                <button className="text-btn primary-btn" onClick={() => setIsModalOpen(true)}>
                  <i className="fa fa-plus" aria-hidden="true" /> Create SSID
                </button>
              </>
            )}
            {activeTab === "schedule" && !isScheduleModalOpen && (
              <>
                <NmsTableSearch
                  value={tableQuery}
                  onChange={setTableQuery}
                  placeholder={localizationStrings?.search || "Search schedules"}
                />
                <button className="icon-btn" title="Refresh" onClick={() => fetchSchedules(networkId)}>
                  <i className="fa fa-refresh" aria-hidden="true" />
                </button>
                <button className="text-btn primary-btn" onClick={() => setIsScheduleModalOpen(true)}>
                  <i className="fa fa-plus" aria-hidden="true" /> Add schedule
                </button>
              </>
            )}
            {activeTab === "filtering" && (
              <button className="text-btn primary-btn" onClick={() => setAddUrl(true)}>
                <i className="fa fa-plus" aria-hidden="true" /> Add URL
              </button>
            )}
          </div>
        </div>

        <div className="inc-card-body">
          <div className="sideTab-container">
            <div className="inc-side-tab">
              {TABS.map((group) => (
                <div key={group.group}>
                  <div className="config-nav-group">{group.group}</div>
                  <ul className="nav-side">
                    {group.items.map((tab) => (
                      <li key={tab.id}>
                        <button
                          type="button"
                          className={`config-nav-item${activeTab === tab.id ? " active" : ""}`}
                          onClick={() => handleTabChange(tab.id)}
                        >
                          {localizationStrings?.[tab.labelKey] || tab.fallback}
                          <i className="fa fa-angle-right" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="inc-side-tabcontainer">
              {activeTab === "wifi" && (
                <>
                  {loading ? (
                    <Loader />
                  ) : (
                    <>
                      <div className="inc-card-table">
                        <table className="full-table m-head">
                          <thead>
                            <tr>
                              {sortHeader("ssid", "SSID")}
                              {sortHeader("band", localizationStrings?.band)}
                              {sortHeader("securityAuthentication", localizationStrings?.security)}
                              {sortHeader("broadcastStatus", localizationStrings?.broadcastStatus)}
                              {sortHeader("fastRoamingSt", localizationStrings?.fastRoaming)}
                              {sortHeader("vlanId", localizationStrings?.vlanId)}
                              <th>{localizationStrings?.action}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageRows.length ? (
                              pageRows.map((wifi) => {
                                const band = bandText(wifi.band);
                                return (
                                  <tr key={wifi.wnId || wifi.ssid}>
                                    <td>{wifi.ssid || "N/A"}</td>
                                    <td>
                                      <div className="band-pills">
                                        {band.includes("2.4GHZ") && <span className="yellow">2.4 GHz</span>}
                                        {band.includes("5GHZ") && <span className="blue">5 GHz</span>}
                                        {!band.includes("2.4GHZ") && !band.includes("5GHZ") && "—"}
                                      </div>
                                    </td>
                                    <td>{wifi.securityAuthentication || "N/A"}</td>
                                    <td>
                                      <span className={`d-status ${wifi.broadcastStatus === 1 ? "online" : "offline"}`}>
                                        {wifi.broadcastStatus === 1 ? "Enabled" : "Disabled"}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`d-status ${wifi.fastRoamingSt === 1 ? "online" : "offline"}`}>
                                        {wifi.fastRoamingSt === 1 ? "Enabled" : "Disabled"}
                                      </span>
                                    </td>
                                    <td>{wifi.vlanId || "N/A"}</td>
                                    <td>
                                      <div className="button-group">
                                        <button
                                          className="icon-btn"
                                          title="Edit"
                                          onClick={() => fetchSSIDDetails(wifi.wnId)}
                                        >
                                          <i className="fa fa-edit" aria-hidden="true" />
                                        </button>
                                        <button
                                          className="icon-btn"
                                          title="Delete"
                                          data-bs-toggle="modal"
                                          data-bs-target="#deleteSsid"
                                          onClick={() => {
                                            setSsid(wifi.ssid);
                                            setWnId(wifi.wnId);
                                          }}
                                        >
                                          <i className="fa fa-trash" aria-hidden="true" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="7">
                                  <div className="nms-empty">{localizationStrings?.noData}</div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {visibleSsids.length > 0 && (
                        <div className="pagination-container">
                          <div className="pagination-info">
                            <p>
                              {localizationStrings?.showing} {(currentPage - 1) * PAGE_SIZE + 1}-
                              {Math.min(currentPage * PAGE_SIZE, visibleSsids.length)} {localizationStrings?.of}{" "}
                              {visibleSsids.length} {localizationStrings?.records}
                            </p>
                          </div>
                          <div className="pagination">
                            <button className="prev-btn" onClick={() => setPage(1)} disabled={currentPage === 1}>
                              {localizationStrings?.firstPage}
                            </button>
                            <button
                              className="prev-btn"
                              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                            >
                              {localizationStrings?.previous}
                            </button>
                            <button
                              className="next-btn"
                              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                            >
                              {localizationStrings?.next}
                            </button>
                            <button
                              className="last-btn"
                              onClick={() => setPage(totalPages)}
                              disabled={currentPage === totalPages}
                            >
                              {localizationStrings?.lastPage}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {activeTab === "schedule" && (
                <>
                  <p className="config-hint">
                    {localizationStrings?.wifiScheduleInfo ||
                      "Create schedules to turn SSIDs on or off at set times."}
                  </p>
                  {isScheduleModalOpen ? (
                    <WifiSchedule
                      isVisible={isScheduleModalOpen}
                      onClose={() => setIsScheduleModalOpen(false)}
                      getList={getSSIDlist}
                      refreshSchedules={fetchSchedules}
                      schedulesList={schedulesList}
                      networkId={networkId}
                    />
                  ) : scheduleRows.length ? (
                    <div className="schedule-wrapper">
                      {scheduleRows.map((item) => (
                        <div className="schedule-item" key={item.scheduleId}>
                          <div className="schedule-icon">
                            <i className="fa fa-clock-o" aria-hidden="true" />
                          </div>
                          <div className="schedule-detail">
                            <h5>{item.scheduleName}</h5>
                            <p>
                              <strong>{localizationStrings?.status}:</strong>{" "}
                              {item.scheduleStatus === 1 ? "Active" : "Inactive"}
                            </p>
                            <p>
                              <strong>{localizationStrings?.createdAt}:</strong>{" "}
                              {item.createdAt
                                ? new Date(
                                    String(item.createdAt).length < 12
                                      ? item.createdAt * 1000
                                      : item.createdAt
                                  ).toLocaleString()
                                : "—"}
                            </p>
                          </div>
                          <div className="schedule-button">
                            <button
                              className="icon-btn"
                              title="Delete"
                              data-bs-toggle="modal"
                              data-bs-target="#deleteSlotModal"
                              onClick={() => setScheduleId(item.scheduleId)}
                            >
                              <i className="fa fa-trash" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="nms-empty">{localizationStrings?.noData}</div>
                  )}
                  {!isScheduleModalOpen && visibleSchedules.length > PAGE_SIZE && (
                    <div className="pagination-container">
                      <div className="pagination">
                        <button
                          className="prev-btn"
                          onClick={() => setSchedulePage((prev) => Math.max(1, prev - 1))}
                          disabled={currentSchedulePage === 1}
                        >
                          {localizationStrings?.previous}
                        </button>
                        <button
                          className="next-btn"
                          onClick={() =>
                            setSchedulePage((prev) => Math.min(totalSchedulePages, prev + 1))
                          }
                          disabled={currentSchedulePage === totalSchedulePages}
                        >
                          {localizationStrings?.next}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {activeTab === "filtering" && (
                <UrlFiltering addUrl={addUrl} setAddUrl={setAddUrl} networkId={networkId} />
              )}
              {activeTab === "policies" && <TrafficPolicies />}
              {activeTab === "roaming" && <FastRoaming networkId={networkId} />}
              {activeTab === "balancing" && <LoadBalancing networkId={networkId} />}
              {activeTab === "management" && <VlanManagement networkId={networkId} />}
              {activeTab === "ipSettings" && <IpSettings networkId={networkId} />}
              {activeTab === "meshSettings" && <MeshSettings networkId={networkId} />}
              {activeTab === "radioConfig" && <RadioConfig networkId={networkId} />}
              {activeTab === "trafficShaping" && <TrafficShaping networkId={networkId} />}

              <div className="modal fade" id="deleteSsid" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content inc-modal">
                    <div className="modal-body success-msg">
                      <img src={errorIcon} alt="" />
                      <p>
                        {localizationStrings?.deleteConfirmation} <strong>{ssid}</strong> SSID?
                      </p>
                      <button
                        className="text-btn primary-btn"
                        data-bs-dismiss="modal"
                        onClick={() => deleteSSID(wnId)}
                      >
                        {localizationStrings?.yes}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal fade" id="deleteSlotModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content inc-modal">
                    <div className="modal-body success-msg">
                      <img src={errorIcon} alt="" />
                      <p>{localizationStrings?.deleteConfirmation}</p>
                      <button
                        className="text-btn primary-btn"
                        data-bs-dismiss="modal"
                        onClick={() => deleteSchedule(scheduleId)}
                      >
                        {localizationStrings?.yes}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {showScheduleStatusModal && (
                <div className="customModal confirm-modal">
                  <div className="modal-dialog modal-dialog-centered small">
                    <div className="modal-content inc-modal">
                      <div className="modal-body success-msg">
                        <img src={successIcon} alt="" />
                        <p>
                          {scheduleDeleteStatus === "success"
                            ? "Schedule deleted successfully."
                            : "Failed to delete schedule. Please try again."}
                        </p>
                        <button
                          className="text-btn primary-btn"
                          onClick={() => setShowScheduleStatusModal(false)}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AddSSIDModal
          getList={getSSIDlist}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}

export default WifiConfig;
